import React, { useState, useEffect, useMemo} from 'react';
import { NavLink  } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Tooltip } from 'react-tooltip';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { jsPDF } from 'jspdf';
import contractService from '../../services/ContractService';
import LayerPdf from '../../components/contract/LayerPdf';
import SlidePreviewPdf from '../../components/contract/SlidePreviewPdf';
import "../../static/css/datatable.css";

const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
    }).format(value);
};

export default function Overview({itemsPerPage = 10 , event}) {
    const { register, watch, formState: { errors } } = useForm();
    const [results, setResults] = useState([]);
    const [totalResults, setTotalResults] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [itemsPerPageLocal, setItemsPerPageLocal] = useState(itemsPerPage);
    const [isLayerPdfOpen, setIsLayerPdfOpen] = useState(false);
    const [type, setType] = useState({});
    const [contractPdf, setContractPdf] = useState(null);
    const [isSlidePrevOpen, setIsSlidePrevOpen] = useState(false);
    const [contractPrev, setContractPrev] = useState(null);

    const search = watch("search", "");
    const totalPages = Math.ceil(results.length / itemsPerPageLocal);
    // Calcola gli elementi da mostrare nella pagina corrente
    const startIndex = (currentPage - 1) * itemsPerPageLocal;
    const endIndex = startIndex + itemsPerPageLocal;
    const currentItems = results.slice(startIndex, endIndex);
    // Funzione di navigazione
    const goToPage = (page) => {
        setCurrentPage(page);
    };

    const fetchContracts= async (search) => {
        try {
            const res = await contractService.getFilterEvent(search,event.id_event);
            const resultsData = res.data || [];
            const total = res.data.length || 0;

            setResults(resultsData); // Salva i risultati
            setTotalResults(total); // Salva il totale dei risultati
            setCurrentPage(1);
            setLoading(false);
            // Calcoliamo dinamicamente itemsPerPageLocal dopo aver ricevuto i dati
            const defaultItemsPerPage = Math.min(itemsPerPage, total);
            setItemsPerPageLocal(defaultItemsPerPage);

        } catch (error) {
            console.error('Error fetching clients', error);
        }
    };

    useEffect(() => {
        fetchContracts(search);
    }, [search]);
    // Funzione per aggiornare il numero di elementi per pagina
    const handleItemsPerPageChange = (event) => {
        setItemsPerPageLocal(parseInt(event.target.value, 10));
        setCurrentPage(1);
    };
        // Funzione per generare le opzioni dinamiche per la select
        const generateItemsPerPageOptions = () => {
            const options = [];
            const maxItems = totalResults;

            if (maxItems <= 2) {
              options.push(2);
            } else if (20 <= maxItems && maxItems <= 50){
              options.push(10, 25, maxItems);
            } else if (maxItems > 50) {
                options.push(10, 25, 50);
            } else {
                // Aggiungiamo anche multipli di 2 tra il minimo e il massimo
                for (let i = 2; i <= maxItems; i *= 2) {
                    if (i < maxItems) options.push(i);
                }
                // Se il numero totale è abbastanza grande, aggiungiamo anche il numero massimo
                if (maxItems > 2) options.push(maxItems);
            }

            return options;
        };

        const getFormattedDate = () => {
          const date = new Date();
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${day}-${month}-${year}`;
        };

        const handleExportPDF = () => {
            const doc = new jsPDF('l', 'mm', 'a4');//configuro con l(stampa orizzontale)
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(12);
            doc.text("Lista Clienti", 14, 10); //Titolo
            doc.autoTable({
                head: [['NR','Azienda','Contatti','Dati Fiscali','Pagamenti','Referente','Tipologia','Note']], // Intestazioni della tabella
                body: results.map((cliente, index) => [index + 1,`${cliente.fullname}\n${cliente.address}\n${cliente.comune} ${cliente.provincia}\n${cliente.cap} ${cliente.nazione}`,
                    `Telefono: ${cliente.phone}\nPec: ${cliente.email_pec}\nEmail: ${cliente.email}\nTel. Referente:${cliente.smartphone}`,
                    `P.Iva: ${cliente.piva}\nCF: ${cliente.tax_code}\nSDI: ${cliente.sdi}`,
                    `${cliente.payment_method}\n${cliente.iban}`,cliente.referent,cliente.customer_type,cliente.note]), // Corpo della tabella
                startY: 20, // Imposta la posizione di inizio della tabella
                theme: 'grid', // Aggiungi griglia alla tabella
            });
            doc.save(`clienti_${getFormattedDate()}.pdf`);
        };

    const totalImport = useMemo(() => {
        return results.reduce((acc, contract) => acc + (contract.amount || 0), 0);
    }, [results]);

    const totalClient = useMemo(() => {
        return new Set(results.map(c => c.clientName)).size;
    }, [results]);

    const toggleLayerPdf = (contract,type) => {
        setContractPdf(contract);
        setType(type);
        setIsLayerPdfOpen(!isLayerPdfOpen);
    };
    const toggleSlidePreview = (contract) => {
        setContractPrev(contract);
        setIsSlidePrevOpen(!isSlidePrevOpen);
    };

  	return (
        <>
            <div className="row text-nowrap">
                <div className="col-md-4 mb-4">
                    <div className="card h-100">
                        <div className="card-body">
                            <div className="card-icon mb-2">
                                <div className="avatar">
                                    <div className="avatar-initial rounded bg-label-primary">
                                        <i className="bx bx-euro bx-lg"></i>
                                    </div>
                                </div>
                            </div>
                            <div className="card-info">
                                <h5 className="card-title mb-2">Totale</h5>
                                <div className="d-flex align-items-baseline gap-1">
                                    <h5 className="text-primary mb-0">{formatCurrency(totalImport)}</h5>
                                </div>
                                <p className="mb-0 text-truncate">Anno di fatturazione corrente.</p>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-4 mb-4">
                    <div className="card">
                        <div className="card-body">
                            <div className="card-icon mb-2">
                                <div className="avatar">
                                    <div className="avatar-initial rounded bg-label-info">
                                        <i className="bx bx-notepad bx-lg"></i>
                                    </div>
                                </div>
                            </div>
                            <div className="card-info">
                                <h5 className="card-title mb-2">Contratti</h5>
                                <div className="d-flex align-items-baseline gap-1">
                                    <h5 className="text-info mb-0">{totalResults}</h5>
                                </div>
                                <p className="mb-0 text-truncate">Vengono considerati quelli nell'anno corrente.</p>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-4 mb-4">
                    <div className="card">
                        <div className="card-body">
                            <div className="card-icon mb-2">
                                <div className="avatar">
                                    <div className="avatar-initial rounded bg-label-warning">
                                        <i className="bx bx-user bx-lg"></i>
                                    </div>
                                </div>
                            </div>
                            <div className="card-info">
                                <h5 className="card-title mb-2">Clienti</h5>
                                <div className="d-flex align-items-baseline gap-1">
                                    <h5 className="text-warning mb-0">{totalClient}</h5>
                                </div>
                                <p className="mb-0 text-truncate">Clienti che partecipano all'evento.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* List Contract  */}
            <div className="card mb-6">
                <div className="card-datatable table-responsive">
                    <div className="dataTables_wrapper dt-bootstrap5 no-footer">
                        <div className="row">
                            <div className="col-md-2">
                                <div className="dataTables_length">
                                    <label>
                                        <select name="DataTables_Table_0_length" className="form-select" id="itemsPerPage"
                                            value={itemsPerPageLocal} onChange={handleItemsPerPageChange}>
                                            {generateItemsPerPageOptions().map((value) => (
                                                <option key={value} value={value}>{value}</option>
                                            ))}
                                        </select>
                                    </label>
                                </div>
                            </div>
                            <div className="col-md-10">
                                <div className="dt-action-buttons text-xl-end text-lg-start text-md-end text-start d-flex align-items-center justify-content-end flex-md-row flex-column mb-6 mb-md-0 mt-n6 mt-md-0">
                                    <input type="hidden" {...register('search')}/> {/* va tolto appena si decide il default e cosa passare come filtri */}
                                    <div className="dataTables_filter dt-buttons btn-group flex-wrap">
                                        <div className="btn-group">
                                           {/* onClick={handleExportPDF} */}
                                            <button className="btn buttons-collection dropdown-toggle btn-label-secondary mx-4" type="button" >
                                                <span><i className="bx bx-export me-2 bx-sm"></i>Export</span>
                                            </button>
                                        </div>
                                        {/* Pulsante per aprire il modulo di aggiunta cliente onClick={toggleSlide}
                                        <button className="btn add-new btn-primary">
                                             <span>
                                                <i className="bx bx-plus bx-sm me-0 me-sm-2"></i>
                                                <span className="d-none d-sm-inline-block">Aggiungi</span>
                                             </span>
                                       </button>*/}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <table className="datatables-users table border-top dataTable no-footer dtr-column">
                            <thead>
                                <tr>
                                    <th>STATO</th>
                                    <th>NR</th>
                                    <th>CLIENTE</th>
                                    <th>TIPOLOGIA</th>
                                    <th>COMMERCIALE</th>
                                    <th>IMPORTO</th>
                                    <th>PROGETTO</th>
                                </tr>
                            </thead>
                            {loading && (
                                <tbody>
                                    <tr className="odd">
                                        <td className="dataTables_empty">Loading...</td>
                                    </tr>
                                </tbody>
                            )}
                            <tbody>
                                {currentItems.map((result,index) => (
                                    <tr key={result.id_contract}>
                                        <td data-tooltip-id={`tooltip-note-${result.id_contract}`}
                                            data-tooltip-content={`${result.note}`}>
                                            {result.status}
                                        </td>
                                        <td data-tooltip-id={`tooltip-date-${result.id_contract}`}
                                            data-tooltip-content={`${result.startDate ? format(new Date(result.startDate), 'dd MMMM yyyy', { locale: it }) : ''}`}>
                                        {(result.status === "Accettato")
                                        ? (
                                            <a onClick={() => toggleLayerPdf(result,"CONTRATTO")}>
                                                {result.number.toString().padStart(3, "0")}
                                            </a>
                                        ): (
                                            <a onClick={() => toggleSlidePreview(result)}>
                                                {result.number.toString().padStart(3, "0")}
                                            </a>
                                        ) }
                                        </td>
                                        <td>
                                            <NavLink to={`/client-detail/${result.id_client}`}>
                                                {result.clientName}
                                            </NavLink>
                                        </td>
                                        <td>{result.contractType}</td>
                                        <td>{result.commercial}</td>
                                        <td>{formatCurrency(result.amount)}</td>
                                        <td>
                                            <a className="dropdown-item" onClick={() => toggleLayerPdf(result,"PROGETTO")}>
                                                <i className="bx bxs-file-pdf bx-lg me-1"></i>
                                            </a>
                                        </td>
                                        <Tooltip id={`tooltip-note-${result.id_contract}`} place="right" className="tooltip-custom" />
                                        <Tooltip id={`tooltip-date-${result.id_contract}`} place="right" className="tooltip-custom" />
                                    </tr>
                                ))}
                             </tbody>
                        </table>
                        <div className="row">
                            <div className="col-sm-12 col-md-6">
                                <div className="dataTables_info" role="status" aria-live="polite">
                                    Risultati ottenuti : {totalResults}
                                </div>
                            </div>
                            <div className="col-sm-12 col-md-6">
                                <div className="dataTables_paginate paging_simple_numbers custom-ryo">
                                    {!loading && (
                                        <div className="pagination">
                                            <button className="page-link" onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}>
                                                <i className="bx bx-chevron-left bx-18px"></i>
                                            </button>
                                            {Array.from({ length: totalPages }, (_, index) => (
                                                <button
                                                  key={index + 1}
                                                  onClick={() => goToPage(index + 1)}
                                                  disabled={currentPage === index + 1}
                                                  className={`page-link ${currentPage === index + 1  ? "current-page" : ""}`}
                                                >
                                                  {index + 1}
                                                </button>
                                            ))}
                                            <button className="page-link" onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}>
                                                <i className="bx bx-chevron-right bx-18px"></i>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {/* Layer */}
                { contractPdf && (
                    <LayerPdf show={isLayerPdfOpen} onClose={toggleLayerPdf}  contract={contractPdf} type={type}/>
                    )
                }
                { contractPrev && (
                    <SlidePreviewPdf show={isSlidePrevOpen} onClose={toggleSlidePreview}  contract={contractPrev}/>
                    )
                }
                {/* Layer */}
            </div>
            {/* /List Contract  */}
        </>
	);
}